import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  type PaginationState,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'

import TanstackTable from 'components/reusable/table/TanstackTable'
import { getFormattedDate } from 'utils'
import { useGetBrokersOperationsBatches } from 'utils/web_api'
import { type BrokersOperationsBatch } from 'utils/web_api.schemas'

import { StatusBadge } from './StatusBadge'

const columnHelper = createColumnHelper<BrokersOperationsBatch>()

const OperationsBatchesList = () => {
  const navigate = useNavigate()
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 1,
    pageSize: 20,
  })
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'created_at', desc: true },
  ])
  const [paginationMetadata, setPaginationMetadata] = useState<{
    total_records: number
    current_page: number
    total_pages: number
    next_page: number | null
    prev_page: number | null
  }>()

  // Build params for autogenerated hook
  const params = useMemo(() => {
    const p: Record<string, unknown> = {
      page: pagination.pageIndex,
      items: pagination.pageSize,
    }

    // Add sorting
    if (sorting.length > 0) {
      const { id, desc } = sorting[0]
      p[`sort[${id}]`] = desc ? 'desc' : 'asc'
    }

    return p
  }, [pagination, sorting])

  const {
    data,
    isLoading,
    isError,
  } = useGetBrokersOperationsBatches(params)

  const columns = useMemo(() => [
    columnHelper.accessor('display_name', {
      header: 'Batch Type',
      cell: ({ row }) => (
        <div className='font-medium text-blue-600'>
          {row.original.display_name}
        </div>
      ),
    }),
    columnHelper.accessor('status', {
      header: 'Status',
      cell: ({ row }) => <StatusBadge status={row.original.status} />,
    }),
    columnHelper.accessor('period_start_date', {
      header: 'Period',
      cell: ({ row }) => {
        const start = row.original.period_start_date
        const end = row.original.period_end_date
        if (!start || !end) return '-'
        return (
          <div className='text-sm'>
            {getFormattedDate(start, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })} -{' '}
            {getFormattedDate(end, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}
          </div>
        )
      },
    }),
    columnHelper.accessor('tasks_count', {
      header: 'Total Tasks',
      cell: ({ row }) => (
        <div className='text-center'>{row.original.tasks_count}</div>
      ),
    }),
    columnHelper.accessor('progress_percentage', {
      header: 'Progress',
      cell: ({ row }) => {
        const percentage = row.original.progress_percentage || 0
        const completed = row.original.tasks_completed_count || 0
        const total = row.original.tasks_count || 0
        return (
          <div className='space-y-1'>
            <div className='text-sm text-gray-600'>
              {completed} / {total} completed
            </div>
            <div className='w-full bg-gray-200 rounded-full h-2'>
              <div
                className='bg-blue-600 h-2 rounded-full transition-all'
                style={{ width: `${percentage}%` }}
              />
            </div>
          </div>
        )
      },
    })
  ], [])

  const table = useReactTable({
    data: data?.data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    manualSorting: true,
    state: {
      pagination,
      sorting,
    },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
  })

  useEffect(() => {
    if (data) {
      setPaginationMetadata(data.pagination)
    }
  }, [data?.pagination])

  return (
    <div className='max-w-[2400px] mx-auto px-4 pb-6'>
      <div className='flex-col w-full h-full space-y-4'>
        <div className='bg-white rounded-lg py-6'>
          <div className='text-2xl font-semibold mb-4'>Operations Batches</div>
        </div>

        <TanstackTable
          table={table}
          isLoading={isLoading}
          isError={isError}
          noDataMessage='No operations batches found.'
          onRowClick={({ row }) => navigate(`/brokers/operations_batches/${row.original.id}`)}
          setSorting={setSorting}
          defaultSortingState={[{ id: 'created_at', desc: true }]}
          paginationLabelSingular='batch'
          paginationLabelPlural='batches'
          paginationMetadata={paginationMetadata}
          onNextPageClick={() =>
            setPagination({
              ...pagination,
              pageIndex: paginationMetadata?.next_page || 1,
            })}
          onPreviousPageClick={() =>
            setPagination({
              ...pagination,
              pageIndex: paginationMetadata?.prev_page || 1,
            })}
        />
      </div>
    </div>
  )
}

export default OperationsBatchesList
